import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from './supabaseClient';

const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
  'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
  'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
  'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire',
  'New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio',
  'Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota',
  'Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia',
  'Wisconsin','Wyoming','Washington D.C.'
];

const DEFAULT_VARIEGATION_PATTERNS = [
  'Half Moon','Marble','Speckled/Splash','Sectoral','Tricolor','Mint','Ghost','Full','Other'
];

const PATTERN_DETAILS = {
  'Half Moon': 'One half of the leaf is solid color, other half green',
  'Marble': 'Swirled, mixed pattern throughout the leaf',
  'Speckled/Splash': 'Random small flecks or splashes of color',
  'Sectoral': 'A defined block or section is a different color',
  'Tricolor': 'Three distinct colors present on the leaf',
  'Mint': 'Pale minty-green coloring throughout',
  'Ghost': 'Very pale, almost white, may green up over time',
  'Full': 'Leaf is entirely or almost entirely variegated',
  'Other': 'Doesn\'t fit the standard categories',
};

const emptyEntry = () => ({
  genus: '',
  common_name: '',
  scientific_name: '',
  catalogEntry: null,
  nameSearch: '',
  committedSearch: '',
  useCustomName: false,
  variegation_level: '',
  variegation_pattern: '',
  sale_type: '',
  sale_size: '',
  tissue_culture_sale_pack_size: '',
  date_sold: new Date().toISOString().split('T')[0],
  sold_price: '',
  source: '',
  photo: null,
  photoPreview: null,
});

const SubmitSale = () => {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [photoConsent, setPhotoConsent] = useState(false);
  const [highlightedFields, setHighlightedFields] = useState({});
  const fieldRefs = useRef({});
  const touchStartY = useRef(0);
  const [catalog, setCatalog] = useState([]);
  const [globalPatterns, setGlobalPatterns] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [submitter, setSubmitter] = useState({ city: '', state: '', country: 'USA' });
  const [entries, setEntries] = useState([emptyEntry()]);
  const isMobile = window.innerWidth <= 600;

  useEffect(() => {
    const fetchCatalog = async () => {
      const { data } = await supabase
        .from('plant_catalog')
        .select('*')
        .neq('is_active', false)
        .order('genus')
        .order('common_name');
      if (data) {
        setCatalog(data);
      }
      setCatalogLoading(false);
    };
    fetchCatalog();

    const fetchPatterns = async () => {
      const { data } = await supabase.from('variegation_patterns').select('*').eq('is_active', true).order('name');
      if (data && data.length > 0) setGlobalPatterns(data);
    };
    fetchPatterns();
  }, []);

  // Filter plants — splits words so "albo monstera" matches "Monstera Albo"
  const getFilteredPlants = (genus, nameSearch) => {
    let plants = catalog;
    if (genus) plants = plants.filter(p => p.genus === genus);
    if (nameSearch && nameSearch.trim()) {
      const words = nameSearch.toLowerCase().trim().split(/\s+/);
      plants = plants.filter(p => {
        const name = (p.common_name || '').toLowerCase();
        const sci = (p.scientific_name || '').toLowerCase();
        return words.every(w => name.includes(w) || sci.includes(w));
      });
    }
    return plants.sort((a, b) => a.common_name.localeCompare(b.common_name));
  };

  const selectCatalogPlant = (index, plant) => {
    const updated = [...entries];
    updated[index] = {
      ...updated[index],
      common_name: plant.common_name,
      genus: plant.genus || updated[index].genus,
      scientific_name: plant.scientific_name || '',
      catalogEntry: plant,
      nameSearch: plant.common_name,
      useCustomName: false,
      variegation_level: '',
      variegation_pattern: '',
    };
    setEntries(updated);
  };

  const handleEntryChange = (index, field, value) => {
    const updated = [...entries];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'sale_type') {
      if (value === 'Tissue Culture (TC)') {
        updated[index].tissue_culture_sale_pack_size = '1';
        updated[index].sale_size = '';
      } else {
        updated[index].tissue_culture_sale_pack_size = '';
        if (value !== 'Potted') updated[index].sale_size = '';
      }
    }
    setEntries(updated);
  };

  const handlePhotoChange = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      const updated = [...entries];
      updated[index] = { ...updated[index], photo: file, photoPreview: URL.createObjectURL(file) };
      setEntries(updated);
    }
  };

  const removePhoto = (index) => {
    const updated = [...entries];
    updated[index] = { ...updated[index], photo: null, photoPreview: null };
    setEntries(updated);
  };

  const addEntry = () => setEntries([...entries, emptyEntry()]);
  const removeEntry = (index) => { if (entries.length === 1) return; setEntries(entries.filter((_, i) => i !== index)); };
  const clearHighlight = (key) => setHighlightedFields(h => ({ ...h, [key]: false }));

  const handleSubmit = async () => {
    const missing = {};
    let firstMissingKey = null;
    const flag = (key) => { missing[key] = true; if (!firstMissingKey) firstMissingKey = key; };

    entries.forEach((e, i) => {
      if (!e.common_name) flag(`${i}_common_name`);
      if (!e.source) flag(`${i}_source`);
      if (!e.sale_type) flag(`${i}_sale_type`);
      if (!e.date_sold) flag(`${i}_date_sold`);
      if (!e.sold_price) flag(`${i}_sold_price`);
      if (e.sale_type === 'Potted' && !e.sale_size) flag(`${i}_sale_size`);
      if (e.sale_type === 'Tissue Culture (TC)' && !e.tissue_culture_sale_pack_size) flag(`${i}_tissue_culture_sale_pack_size`);
    });

    if (Object.keys(missing).length > 0) {
      setHighlightedFields(missing);
      setError('Please fill in all required fields highlighted below.');
      if (firstMissingKey && fieldRefs.current[firstMissingKey]) {
        fieldRefs.current[firstMissingKey].scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    const hasPhotos = entries.some(e => e.photo);
    if (hasPhotos && !photoConsent) { setError('Please confirm you own the rights to the photo(s).'); return; }

    setLoading(true); setError(null);
    const rows = [];
    for (const entry of entries) {
      let image_url = '';
      if (entry.photo) {
        const fileExt = entry.photo.name.split('.').pop().toLowerCase();
        const safeExt = ['jpg','jpeg','png','webp','gif'].includes(fileExt) ? fileExt : 'jpg';
        const fileName = `${Date.now()}_${entry.common_name.replace(/[^a-zA-Z0-9]/g, '_')}.${safeExt}`;
        const { error: uploadError } = await supabase.storage
          .from('plant-images')
          .upload(fileName, entry.photo, { contentType: entry.photo.type || 'image/jpeg', upsert: false });
        if (uploadError) {
          console.error('Upload error:', uploadError);
          setError(`Photo upload failed: ${uploadError.message}`);
          setLoading(false);
          return;
        }
        const { data: urlData } = supabase.storage.from('plant-images').getPublicUrl(fileName);
        image_url = urlData.publicUrl;
      }
      rows.push({
        common_name: entry.common_name,
        genus: entry.genus || null,
        scientific_name: entry.scientific_name || null,
        variegation_level: entry.catalogEntry?.is_variegated ? (entry.variegation_level || null) : null,
        variegation_pattern: entry.catalogEntry?.is_variegated ? (entry.variegation_pattern || null) : null,
        sale_type: entry.sale_type,
        sale_size: entry.sale_type === 'Potted' ? (entry.sale_size || null) : null,
        tissue_culture_sale_pack_size: entry.sale_type === 'Tissue Culture (TC)' ? (entry.tissue_culture_sale_pack_size || null) : null,
        date_sold: entry.date_sold,
        sold_price: entry.sold_price,
        source: entry.source,
        image_url,
        city: submitter.city || null,
        state: submitter.state || null,
        country: submitter.country,
        status: 'pending',
      });
    }
    const { error: insertError } = await supabase.from('plants').insert(rows);
    if (insertError) { setError('Something went wrong. Please try again.'); console.error(insertError); }
    else { setSubmitted(true); }
    setLoading(false);
  };

  const inp = (highlighted) => ({
    width: '100%', padding: '14px 16px',
    border: `2px solid ${highlighted ? '#c0392b' : '#c4a882'}`,
    borderRadius: '12px', fontSize: '16px',
    background: highlighted ? '#fff5f5' : '#ffffff',
    color: '#1e2d1f', outline: 'none', fontFamily: 'Lato, sans-serif',
    boxSizing: 'border-box', appearance: 'auto', WebkitAppearance: 'auto',
  });

  const labelStyle = (highlighted) => ({
    display: 'block', fontSize: '11px', letterSpacing: '2px',
    textTransform: 'uppercase', color: highlighted ? '#c0392b' : '#4a7c59',
    fontWeight: 700, marginBottom: '6px', fontFamily: 'Lato, sans-serif',
  });

  const fieldWrap = { marginBottom: '18px' };
  const sectionStyle = { background: '#ffffff', borderRadius: '20px', padding: '24px', boxShadow: '0 2px 16px rgba(44,74,46,0.1)', marginBottom: '24px', border: '1px solid rgba(143,185,154,0.3)' };

  if (submitted) {
    return (
      <div style={{ maxWidth: '600px', margin: '80px auto', padding: '0 16px', textAlign: 'center' }}>
        <div style={{ fontSize: '64px', marginBottom: '24px' }}>🌿</div>
        <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: '32px', color: '#1e3d20', marginBottom: '12px' }}>Thank You!</h1>
        <p style={{ color: '#4a5e4b', fontFamily: 'Lato, sans-serif', fontSize: '16px', marginBottom: '32px' }}>
          Your {entries.length} sale{entries.length !== 1 ? 's have' : ' has'} been submitted for review!
        </p>
        <button onClick={() => navigate('/')} style={{ background: 'linear-gradient(135deg, #2c4a2e, #4a7c59)', color: '#f5f0e8', border: 'none', borderRadius: '20px', padding: '14px 32px', fontSize: '15px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>
          Back to Home
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '760px', margin: '0 auto', padding: isMobile ? '24px 16px 80px' : '40px 16px 80px' }}>
      <div style={{ marginBottom: '32px' }}>
        <div style={{ fontSize: '11px', letterSpacing: '4px', color: '#4a7c59', textTransform: 'uppercase', marginBottom: '8px', fontFamily: 'Lato, sans-serif' }}>🌿 Community Data</div>
        <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: isMobile ? '28px' : '36px', color: '#1e3d20', marginBottom: '8px' }}>Submit Sales</h1>
        <p style={{ color: '#4a5e4b', fontFamily: 'Lato, sans-serif', fontSize: '15px' }}>Add one or multiple sales at once. All submissions are reviewed before going live.</p>
      </div>

      {catalogLoading && (
        <div style={{ textAlign: 'center', padding: '60px', fontFamily: 'Lato, sans-serif', color: '#4a7c59' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>🌿</div>
          <p>Loading plant catalog...</p>
        </div>
      )}

      {!catalogLoading && entries.map((entry, index) => (
        <div key={index} style={{ ...sectionStyle, borderTop: '4px solid #4a7c59' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', color: '#1e3d20', margin: 0 }}>🌱 Sale #{index + 1}</h2>
            {entries.length > 1 && (
              <button onClick={() => removeEntry(index)} style={{ background: 'transparent', border: '1px solid #c0392b', color: '#c0392b', borderRadius: '20px', padding: '6px 16px', fontSize: '12px', cursor: 'pointer', fontFamily: 'Lato, sans-serif', fontWeight: 700 }}>Remove</button>
            )}
          </div>

          {/* PLANT SELECTION — typeahead autocomplete */}
          <div style={{ background: '#f7f3ec', borderRadius: '14px', padding: '16px', marginBottom: '20px', border: `2px solid ${highlightedFields[`${index}_common_name`] ? '#c0392b' : 'rgba(143,185,154,0.3)'}` }}>
            <label style={{ ...labelStyle(highlightedFields[`${index}_common_name`]), color: '#2c4a2e', fontSize: '12px' }}>
              Select Plant *{highlightedFields[`${index}_common_name`] && <span style={{ fontSize: '11px', marginLeft: '6px', color: '#c0392b' }}>Required</span>}
            </label>

            {/* Selected plant display */}
            {entry.common_name && !entry.useCustomName ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'linear-gradient(135deg, #f0f7f1, #e8f4eb)', borderRadius: '10px', padding: '12px 16px', border: '2px solid #4a7c59' }}>
                <div>
                  <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '16px', color: '#1e3d20', fontWeight: 700 }}>{entry.common_name}</div>
                  {entry.scientific_name && <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '12px', color: '#4a5e4b', fontStyle: 'italic', marginTop: '2px' }}>{entry.scientific_name}</div>}
                </div>
                <button type="button" onClick={() => {
                  const updated = [...entries];
                  updated[index] = { ...updated[index], common_name: '', scientific_name: '', catalogEntry: null, nameSearch: '', useCustomName: false };
                  setEntries(updated);
                }} style={{ background: 'transparent', border: '1px solid #c0392b', color: '#c0392b', borderRadius: '20px', padding: '4px 12px', fontSize: '12px', cursor: 'pointer', fontFamily: 'Lato, sans-serif', fontWeight: 700, flexShrink: 0, marginLeft: '12px' }}>✕ Change</button>
              </div>
            ) : entry.useCustomName ? (
              <div>
                <input ref={el => fieldRefs.current[`${index}_common_name`] = el}
                  placeholder="Plant name e.g. Monstera Albo"
                  value={entry.common_name}
                  onChange={(e) => handleEntryChange(index, 'common_name', e.target.value)}
                  style={{ ...inp(highlightedFields[`${index}_common_name`]), marginBottom: '8px' }}
                />
                <input placeholder="Genus e.g. Monstera" value={entry.genus}
                  onChange={(e) => handleEntryChange(index, 'genus', e.target.value)}
                  style={{ ...inp(false), marginBottom: '8px' }} />
                <input placeholder="Scientific name (optional)" value={entry.scientific_name}
                  onChange={(e) => handleEntryChange(index, 'scientific_name', e.target.value)}
                  style={{ ...inp(false), marginBottom: '8px' }} />
                <button type="button" onClick={() => {
                  const updated = [...entries];
                  updated[index] = { ...updated[index], useCustomName: false, common_name: '', nameSearch: '' };
                  setEntries(updated);
                }} style={{ background: 'transparent', border: 'none', color: '#4a7c59', cursor: 'pointer', fontFamily: 'Lato, sans-serif', fontSize: '13px', fontWeight: 700 }}>← Back to search</button>
              </div>
            ) : (
              <div style={{ position: 'relative' }}>
                <input
                  ref={el => fieldRefs.current[`${index}_common_name`] = el}
                  placeholder="🔍 Type plant name to search..."
                  value={entry.nameSearch}
                  onChange={(e) => {
                    const updated = [...entries];
                    updated[index] = { ...updated[index], nameSearch: e.target.value };
                    setEntries(updated);
                  }}
                  style={{ ...inp(highlightedFields[`${index}_common_name`]), marginBottom: 0 }}
                  autoComplete="off"
                />
                {/* Results list */}
                {entry.nameSearch.trim().length >= 2 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '2px solid #4a7c59', borderRadius: '0 0 12px 12px', maxHeight: '220px', overflowY: 'auto', zIndex: 100, boxShadow: '0 8px 24px rgba(44,74,46,0.15)' }}>
                    {getFilteredPlants('', entry.nameSearch).slice(0, 20).map(p => (
                      <div key={p.id}
                        onMouseDown={(e) => { e.preventDefault(); selectCatalogPlant(index, p); clearHighlight(`${index}_common_name`); }}
                        onTouchStart={(e) => { touchStartY.current = e.touches[0].clientY; }}
                        onTouchEnd={(e) => {
                          const dy = Math.abs(e.changedTouches[0].clientY - touchStartY.current);
                          if (dy < 10) { e.preventDefault(); selectCatalogPlant(index, p); clearHighlight(`${index}_common_name`); }
                        }}
                        style={{ padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid rgba(143,185,154,0.2)', fontFamily: 'Lato, sans-serif' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f0f7f1'}
                        onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                      >
                        <div style={{ fontSize: '14px', color: '#1e3d20', fontWeight: 700 }}>{p.common_name}</div>
                        {p.scientific_name && <div style={{ fontSize: '11px', color: '#8b9e8c', fontStyle: 'italic' }}>{p.scientific_name}</div>}
                        <div style={{ fontSize: '10px', color: '#4a7c59', textTransform: 'uppercase', letterSpacing: '1px', marginTop: '2px' }}>{p.genus}</div>
                      </div>
                    ))}
                    {getFilteredPlants('', entry.nameSearch).length === 0 && (
                      <div style={{ padding: '12px 16px', fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#8b9e8c' }}>No plants found</div>
                    )}
                    <div
                      onMouseDown={(e) => { e.preventDefault(); const updated = [...entries]; updated[index] = { ...updated[index], useCustomName: true, nameSearch: '' }; setEntries(updated); }}
                      onTouchStart={(e) => { touchStartY.current = e.touches[0].clientY; }}
                      onTouchEnd={(e) => {
                        const dy = Math.abs(e.changedTouches[0].clientY - touchStartY.current);
                        if (dy < 10) { e.preventDefault(); const updated = [...entries]; updated[index] = { ...updated[index], useCustomName: true, nameSearch: '' }; setEntries(updated); }
                      }}
                      style={{ padding: '12px 16px', cursor: 'pointer', background: '#f7f3ec', fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#4a7c59', fontWeight: 700, borderTop: '2px solid rgba(143,185,154,0.3)' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#eef5ef'}
                      onMouseLeave={e => e.currentTarget.style.background = '#f7f3ec'}
                    >➕ My plant isn't listed — add it manually</div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Selected plant info */}
          {entry.common_name && !entry.useCustomName && (
            <div style={{ ...fieldWrap, padding: '12px 16px', background: 'linear-gradient(135deg, #f0f7f1, #e8f4eb)', borderRadius: '10px', borderLeft: '3px solid #4a7c59' }}>
              <span style={{ fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: '#4a7c59', fontWeight: 700, fontFamily: 'Lato, sans-serif', display: 'block', marginBottom: '4px' }}>Selected Plant</span>
              <span style={{ fontFamily: 'Playfair Display, serif', fontSize: '16px', color: '#1e3d20', fontWeight: 700 }}>{entry.common_name}</span>
              {entry.scientific_name && <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#4a5e4b', fontStyle: 'italic', display: 'block', marginTop: '2px' }}>{entry.scientific_name}</span>}
            </div>
          )}

          {/* VARIEGATION */}
          {entry.catalogEntry?.is_variegated && (
            <div style={{ ...fieldWrap, background: '#fdf8ff', borderRadius: '12px', padding: '16px', border: '1px solid rgba(124,74,124,0.2)' }}>
              <div style={fieldWrap}>
                <label style={{ ...labelStyle(false), color: '#7c4a7c' }}>Variegation Level (optional)</label>
                <select value={entry.variegation_level} onChange={(e) => handleEntryChange(index, 'variegation_level', e.target.value)} style={{ ...inp(false), background: '#fff' }}>
                  <option value="">Select level...</option>
                  <option value="Low">Low — mostly green</option>
                  <option value="Mid">Mid — balanced mix</option>
                  <option value="High">High — heavily variegated</option>
                </select>
              </div>
              <div>
                <label style={{ ...labelStyle(false), color: '#7c4a7c' }}>Variegation Pattern (optional)</label>
                <select value={entry.variegation_pattern} onChange={(e) => handleEntryChange(index, 'variegation_pattern', e.target.value)} style={{ ...inp(false), background: '#fff' }}>
                  <option value="">Select pattern...</option>
                  {(entry.catalogEntry?.variegation_options?.length > 0
                    ? entry.catalogEntry.variegation_options
                    : globalPatterns.length > 0 ? globalPatterns.map(p => p.name) : DEFAULT_VARIEGATION_PATTERNS
                  ).map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                {entry.variegation_pattern && (
                  <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '11px', color: '#7c4a7c', marginTop: '4px', marginBottom: 0, lineHeight: 1.4 }}>
                    – {(globalPatterns.find(p => p.name === entry.variegation_pattern)?.description) || PATTERN_DETAILS[entry.variegation_pattern] || ''}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* WHERE SOLD */}
          <div style={fieldWrap}>
            <label style={labelStyle(highlightedFields[`${index}_source`])}>
              Where Was It Sold? *{highlightedFields[`${index}_source`] && <span style={{ fontSize: '11px', marginLeft: '6px' }}>Required</span>}
            </label>
            <select value={entry.source} ref={el => fieldRefs.current[`${index}_source`] = el}
              onChange={(e) => { handleEntryChange(index, 'source', e.target.value); clearHighlight(`${index}_source`); }}
              style={inp(highlightedFields[`${index}_source`])}>
              <option value="">Select platform...</option>
              <option value="Palmstreet">Palmstreet</option>
              <option value="Facebook Marketplace">Facebook Marketplace</option>
              <option value="Etsy">Etsy</option>
              <option value="eBay">eBay</option>
              <option value="Instagram">Instagram</option>
              <option value="Local Sale">Local Sale / In Person</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* SALE TYPE */}
          <div style={fieldWrap}>
            <label style={labelStyle(highlightedFields[`${index}_sale_type`])}>
              Sale Type *{highlightedFields[`${index}_sale_type`] && <span style={{ fontSize: '11px', marginLeft: '6px' }}>Required</span>}
            </label>
            <select value={entry.sale_type} ref={el => fieldRefs.current[`${index}_sale_type`] = el}
              onChange={(e) => { handleEntryChange(index, 'sale_type', e.target.value); clearHighlight(`${index}_sale_type`); }}
              style={inp(highlightedFields[`${index}_sale_type`])}>
              <option value="">Select type...</option>
              <option value="Potted">Potted</option>
              <option value="Cutting">Cutting</option>
              <option value="Plug/Starter">Plug/Starter</option>
              <option value="Corm">Corm</option>
              <option value="Tissue Culture (TC)">Tissue Culture (TC)</option>
            </select>
          </div>

          {/* POT SIZE */}
          {entry.sale_type === 'Potted' && (
            <div style={fieldWrap}>
              <label style={labelStyle(highlightedFields[`${index}_sale_size`])}>
                Pot Size *{highlightedFields[`${index}_sale_size`] && <span style={{ fontSize: '11px', marginLeft: '6px' }}>Required</span>}
              </label>
              <select value={entry.sale_size} ref={el => fieldRefs.current[`${index}_sale_size`] = el}
                onChange={(e) => { handleEntryChange(index, 'sale_size', e.target.value); clearHighlight(`${index}_sale_size`); }}
                style={inp(highlightedFields[`${index}_sale_size`])}>
                <option value="">Select pot size...</option>
                {['2','3','4','6','8','10','12'].map(s => <option key={s} value={s}>{s}"</option>)}
              </select>
            </div>
          )}

          {/* TC PACK SIZE */}
          {entry.sale_type === 'Tissue Culture (TC)' && (
            <div style={fieldWrap}>
              <label style={labelStyle(highlightedFields[`${index}_tissue_culture_sale_pack_size`])}>
                TC Pack Size *{highlightedFields[`${index}_tissue_culture_sale_pack_size`] && <span style={{ fontSize: '11px', marginLeft: '6px' }}>Required</span>}
              </label>
              <select value={entry.tissue_culture_sale_pack_size}
                ref={el => fieldRefs.current[`${index}_tissue_culture_sale_pack_size`] = el}
                onChange={(e) => { handleEntryChange(index, 'tissue_culture_sale_pack_size', e.target.value); clearHighlight(`${index}_tissue_culture_sale_pack_size`); }}
                style={inp(highlightedFields[`${index}_tissue_culture_sale_pack_size`])}>
                <option value="">Select pack size...</option>
                {Array.from({ length: 20 }, (_, i) => i + 1).map(n => (
                  <option key={n} value={String(n)}>{n} {n === 1 ? 'plant' : 'plants'}</option>
                ))}
              </select>
            </div>
          )}

          {/* DATE SOLD */}
          <div style={fieldWrap}>
            <label style={labelStyle(highlightedFields[`${index}_date_sold`])}>
              Date Sold *{highlightedFields[`${index}_date_sold`] && <span style={{ fontSize: '11px', marginLeft: '6px' }}>Required</span>}
            </label>
            <input type="date" value={entry.date_sold}
              ref={el => fieldRefs.current[`${index}_date_sold`] = el}
              onChange={(e) => { handleEntryChange(index, 'date_sold', e.target.value); clearHighlight(`${index}_date_sold`); }}
              max={new Date().toISOString().split('T')[0]}
              style={{ ...inp(highlightedFields[`${index}_date_sold`]), colorScheme: 'light' }} />
          </div>

          {/* SALE PRICE */}
          <div style={fieldWrap}>
            <label style={labelStyle(highlightedFields[`${index}_sold_price`])}>
              Sale Price *{highlightedFields[`${index}_sold_price`] && <span style={{ fontSize: '11px', marginLeft: '6px' }}>Required</span>}
            </label>
            <input type="number" step="0.01" min="0" value={entry.sold_price}
              ref={el => fieldRefs.current[`${index}_sold_price`] = el}
              onChange={(e) => { handleEntryChange(index, 'sold_price', e.target.value); clearHighlight(`${index}_sold_price`); }}
              placeholder="e.g. 45.00"
              onWheel={(e) => e.target.blur()}
              style={inp(highlightedFields[`${index}_sold_price`])} />
          </div>

          {/* PHOTO */}
          <div style={fieldWrap}>
            <label style={labelStyle(false)}>Photo (optional)</label>
            <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '2px dashed #4a7c59', borderRadius: '12px', padding: '20px', cursor: 'pointer', background: '#f7f3ec' }}>
              <input type="file" accept="image/*" onChange={(e) => handlePhotoChange(index, e)} style={{ display: 'none' }} />
              {entry.photoPreview
                ? <img src={entry.photoPreview} alt="Preview" style={{ maxHeight: '140px', maxWidth: '100%', borderRadius: '8px', objectFit: 'cover' }} />
                : <><div style={{ fontSize: '28px', marginBottom: '8px' }}>📸</div><div style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#4a7c59', fontWeight: 700 }}>Tap to upload a photo</div></>
              }
            </label>
            {entry.photoPreview && (
              <button onClick={() => removePhoto(index)} style={{ marginTop: '8px', background: 'transparent', border: 'none', color: '#c0392b', cursor: 'pointer', fontFamily: 'Lato, sans-serif', fontSize: '12px' }}>Remove photo</button>
            )}
          </div>
        </div>
      ))}

      {!catalogLoading && (
        <button onClick={addEntry} style={{ width: '100%', padding: '16px', background: 'transparent', border: '2px dashed #4a7c59', color: '#4a7c59', borderRadius: '16px', fontSize: '14px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'Lato, sans-serif', marginBottom: '24px' }}>
          + Add Another Sale
        </button>
      )}

      {/* LOCATION */}
      <div style={sectionStyle}>
        <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', color: '#1e3d20', marginBottom: '8px' }}>📍 Location</h2>
        <p style={{ fontSize: '13px', color: '#4a5e4b', fontFamily: 'Lato, sans-serif', marginBottom: '20px' }}>Applied to all sales in this submission.</p>
        <div style={fieldWrap}>
          <label style={labelStyle(false)}>City (optional)</label>
          <input value={submitter.city} onChange={(e) => setSubmitter({ ...submitter, city: e.target.value })} placeholder="e.g. Miami" style={inp(false)} />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle(false)}>State</label>
          <select value={submitter.state} onChange={(e) => setSubmitter({ ...submitter, state: e.target.value })} style={inp(false)}>
            <option value="">Select state...</option>
            {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            <option value="International">International</option>
          </select>
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle(false)}>Country</label>
          <select value={submitter.country} onChange={(e) => setSubmitter({ ...submitter, country: e.target.value })} style={inp(false)}>
            <option value="USA">USA</option>
            <option value="Canada">Canada</option>
            <option value="UK">UK</option>
            <option value="Australia">Australia</option>
            <option value="Germany">Germany</option>
            <option value="France">France</option>
            <option value="Netherlands">Netherlands</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>

      {entries.some(e => e.photo) && (
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px', padding: '16px', background: '#f7f3ec', borderRadius: '12px', cursor: 'pointer' }}>
          <input type="checkbox" checked={photoConsent} onChange={(e) => setPhotoConsent(e.target.checked)} style={{ marginTop: '3px', width: '20px', height: '20px', flexShrink: 0 }} />
          <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#4a5e4b', lineHeight: 1.5 }}>
            I confirm I took these photos myself (or have permission) and grant True Plant Value permission to display them on the site.
          </span>
        </label>
      )}

      {error && <p style={{ color: '#c0392b', fontFamily: 'Lato, sans-serif', marginBottom: '16px', fontWeight: 700, padding: '12px', background: '#fff5f5', borderRadius: '10px' }}>{error}</p>}

      <button onClick={handleSubmit} disabled={loading || catalogLoading}
        style={{ width: '100%', padding: '18px', background: loading ? '#8fb99a' : 'linear-gradient(135deg, #2c4a2e, #4a7c59)', color: '#f5f0e8', border: 'none', borderRadius: '16px', fontSize: '16px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'Lato, sans-serif' }}>
        {loading ? 'Submitting...' : `Submit ${entries.length} Sale${entries.length !== 1 ? 's' : ''}`}
      </button>
    </div>
  );
};

export default SubmitSale;